import React, { useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Avatar, 
  Button, 
  Link,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import { chatStorageService } from '../../services';

// Styled message bubbles
const MessageBubble = styled(Paper)(({ theme, sender, isSystem }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2),
  maxWidth: '80%',
  // full width on small screens
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
  wordBreak: 'break-word',
  marginBottom: theme.spacing(1),
  ...(isSystem ? {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    alignSelf: 'center',
    width: '90%',
    // full width on small screens for system messages
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    borderRadius: theme.spacing(1),
  } : sender === 'bot' ? {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    alignSelf: 'flex-start',
    borderTopLeftRadius: theme.spacing(0.5),
  } : {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    alignSelf: 'flex-end',
    borderTopRightRadius: theme.spacing(0.5),
  }),
}));

const ChatMessageList = ({ messages }) => {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  
  // Auto-scroll to the bottom when new messages are added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages]);
  
  // Save messages to localStorage when they change
  useEffect(() => {
    if (messages && messages.length > 0) {
      chatStorageService.saveMessages(messages);
    }
  }, [messages]);
  
  return (
    <Box 
      ref={containerRef}
      sx={{
        flex: 1,
        p: 2,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {messages.map((message) => (
        <Box
          key={message.id}
          sx={{
            display: 'flex',
            flexDirection: message.isSystem 
              ? 'column' 
              : message.sender === 'bot' 
                ? 'row' 
                : 'row-reverse',
            mb: 2,
            alignItems: message.isSystem ? 'center' : 'flex-start',
            width: '100%',
          }}
        >
          {!message.isSystem && (
            <Avatar 
              sx={{ 
                bgcolor: message.sender === 'bot' ? 'secondary.main' : 'primary.main',
                mr: message.sender === 'bot' ? 1 : 0,
                ml: message.sender === 'bot' ? 0 : 1,
                width: 32,
                height: 32,
              }}
            >
              {message.sender === 'bot' ? <SmartToyIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
            </Avatar>
          )}
          
          <MessageBubble sender={message.sender} data-is-system={message.isSystem} elevation={1}>
            {message.isTemporary ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {message.text}
                </Typography>
              </Box>
            ) : message.isSystem && message.loginUrl ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                width: '100%',
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <InfoIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="body1" fontWeight="medium">
                    {message.text}
                  </Typography>
                </Box>
                
                <Button 
                  variant="contained" 
                  color="primary"
                  size="medium" 
                  href={message.loginUrl}
                  sx={{ mt: 1, px: 3 }}
                >
                  Iniciar sesión ahora
                </Button>
              </Box>
            ) : (
              <>
                <Typography variant="body2">
                  {message.text}
                </Typography>
                
                {message.loginUrl && (
                  <Button 
                    variant="contained" 
                    size="small" 
                    href={message.loginUrl}
                    target="_blank"
                    sx={{ mt: 1 }}
                  >
                    Iniciar sesión
                  </Button>
                )}
              </>
            )}
          </MessageBubble>
        </Box>
      ))}
      <div ref={messagesEndRef} />
    </Box>
  );
};

export default ChatMessageList;
